import { ExpertDisplayName } from "@/components/display/ExpertDisplayName";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Tabs, TabsDummyButton } from "@/components/ui/tabs";
import { EXPERT_TYPE_LABELS } from "@/consts/experts";
import { cn } from "@/lib/utils";
import { Expert } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import React, { useMemo, useRef, useState } from "react";
import { ExpertAvatar } from "../ExpertAvatar";
import { useExpertSelection } from "./ExpertSelectionContext";

type ExpertToInputProps = {
  className?: string;
};

type ExpertTypeFilter = "all" | Expert["expertType"];

const EXPERT_TYPE_FILTERS: { value: ExpertTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  ...(
    Object.entries(EXPERT_TYPE_LABELS).filter(
      ([type]) => type !== "interviewer",
    ) as [Expert["expertType"], string][]
  ).map(([value, label]) => ({
    value,
    label,
  })),
];

const matchesSearch = (expert: Expert, searchText: string) =>
  searchText.trim() === "" ||
  expert.name.toLowerCase().includes(searchText.trim().toLowerCase());

const matchesTypeFilter = (expert: Expert, typeFilter: ExpertTypeFilter) =>
  typeFilter === "all" || expert.expertType === typeFilter;

const MAX_CHIP_NAME_LENGTH = 32;

const truncateChipName = (name: string) =>
  name.length > MAX_CHIP_NAME_LENGTH
    ? `${name.slice(0, MAX_CHIP_NAME_LENGTH)}...`
    : name;

export const ExpertToInput: React.FC<ExpertToInputProps> = ({ className }) => {
  const {
    kitExperts,
    groupExperts,
    selectedExperts,
    selectedExpertUuids,
    selectExpert,
    unselectExpert,
    max,
    disableSelection,
  } = useExpertSelection();

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [expertTypeFilter, setExpertTypeFilter] =
    useState<ExpertTypeFilter>("all");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const hasReachedMax = selectedExperts.length >= max;

  const kitExpertUuids = useMemo(
    () => new Set(kitExperts.map((expert) => expert.uuid)),
    [kitExperts],
  );

  const projectExperts = useMemo(
    () =>
      kitExperts.filter(
        (expert) =>
          matchesSearch(expert, searchText) &&
          matchesTypeFilter(expert, expertTypeFilter),
      ),
    [kitExperts, searchText, expertTypeFilter],
  );

  const groupOnlyExperts = useMemo(
    () =>
      groupExperts.filter(
        (expert) =>
          !kitExpertUuids.has(expert.uuid) &&
          matchesSearch(expert, searchText) &&
          matchesTypeFilter(expert, expertTypeFilter),
      ),
    [groupExperts, kitExpertUuids, searchText, expertTypeFilter],
  );

  const noResults =
    projectExperts.length === 0 && groupOnlyExperts.length === 0;

  const unlinkedSelectedExperts = useMemo(
    () => selectedExperts.filter((expert) => !kitExpertUuids.has(expert.uuid)),
    [selectedExperts, kitExpertUuids],
  );

  const handleOpen = () => {
    if (!disableSelection) setOpen(true);
  };

  const handleSelectExpert = (expert: Expert) => {
    if (selectedExpertUuids.has(expert.uuid)) {
      unselectExpert(expert.uuid);
      return;
    }
    if (max > 1 && hasReachedMax) return;
    selectExpert(expert.uuid);
    setSearchText("");
    inputRef.current?.focus();
  };

  const handleRemoveExpert = (
    e: React.MouseEvent<HTMLButtonElement>,
    expertUuid: string,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    unselectExpert(expertUuid);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Tab" && !e.shiftKey && open) {
      const firstFocusable = contentRef.current?.querySelector<HTMLElement>(
        "button:not(:disabled)",
      );
      if (firstFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  const focusElementAfterInput = () => {
    const input = inputRef.current;
    if (!input) return false;

    const focusable = Array.from(
      document.querySelectorAll<HTMLElement>(
        'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    );
    const index = focusable.indexOf(input);
    const next = index >= 0 ? focusable[index + 1] : undefined;
    if (!next) return false;

    next.focus();
    return true;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={containerRef}
          className={cn(
            "flex cursor-text flex-row gap-4 py-2 text-sm",
            className,
          )}
          onClick={handleOpen}
        >
          <div className="text-foreground-5 flex items-center uppercase">
            To:
          </div>
          <div className="flex flex-1 flex-row flex-wrap items-center gap-2">
            {selectedExperts.map((expert) => (
              <div
                key={expert.uuid}
                className={cn(
                  buttonVariants({ variant: "secondary" }),
                  "bg-background px-2",
                )}
              >
                <ExpertDisplayName
                  expert={{ ...expert, name: truncateChipName(expert.name) }}
                />
                {!disableSelection && (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Remove Expert ${expert.name}`}
                    title={`Remove Expert ${expert.name}`}
                    onClick={(e) => handleRemoveExpert(e, expert.uuid)}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                )}
              </div>
            ))}
            <input
              ref={inputRef}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onFocus={handleOpen}
              onKeyDown={handleInputKeyDown}
              disabled={disableSelection}
              placeholder={"Search Experts..."}
              className="text-foreground placeholder:text-foreground-5 min-w-32 flex-1 bg-transparent py-1 outline-none placeholder:italic"
            />
          </div>
          <div className="text-foreground-5 mt-[7px] flex shrink-0 items-center text-xs whitespace-nowrap">
            {selectedExperts.length} out of {max}
          </div>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="flex max-h-96 w-120 flex-col overflow-hidden p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          focusElementAfterInput();
        }}
        onFocusOutside={(e) => {
          if (containerRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (containerRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
      >
        <div ref={contentRef} className="contents">
          <Tabs value={expertTypeFilter} className="shrink-0 gap-0 pb-2">
            <div className="flex flex-row gap-1 overflow-x-auto">
              {EXPERT_TYPE_FILTERS.map(({ value, label }) => (
                <TabsDummyButton
                  key={value}
                  isActive={expertTypeFilter === value}
                  onMouseUp={() => setExpertTypeFilter(value)}
                  className="h-7 px-3 text-xs"
                >
                  {label}
                </TabsDummyButton>
              ))}
            </div>
          </Tabs>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            {noResults && (
              <div className="text-foreground-5 flex items-center justify-center p-4 text-sm">
                No experts found
              </div>
            )}
            {projectExperts.length > 0 && (
              <ExpertOptionGroup
                label="Workbook Experts"
                experts={projectExperts}
                selectedExpertUuids={selectedExpertUuids}
                disableUnselected={max > 1 && hasReachedMax}
                onSelect={handleSelectExpert}
              />
            )}
            {groupOnlyExperts.length > 0 && (
              <ExpertOptionGroup
                label="Workspace Experts"
                experts={groupOnlyExperts}
                selectedExpertUuids={selectedExpertUuids}
                disableUnselected={max > 1 && hasReachedMax}
                onSelect={handleSelectExpert}
                showAddToProjectTag
              />
            )}
          </div>
          {unlinkedSelectedExperts.length > 0 && (
            <div className="text-accent bg-accent-1 border-border shrink-0 rounded-lg border-t px-3 pt-2 pb-1 text-xs">
              {unlinkedSelectedExperts.map((expert) => expert.name).join(", ")}{" "}
              will join the workbook when you send your first message.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

type ExpertOptionGroupProps = {
  label: string;
  experts: Expert[];
  selectedExpertUuids: Set<string>;
  disableUnselected: boolean;
  onSelect: (expert: Expert) => void;
  showAddToProjectTag?: boolean;
};

const ExpertOptionGroup: React.FC<ExpertOptionGroupProps> = ({
  label,
  experts,
  selectedExpertUuids,
  disableUnselected,
  onSelect,
  showAddToProjectTag,
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-foreground-5 px-2 py-1 text-xs font-semibold uppercase">
        {label}
      </div>
      {experts.map((expert) => {
        const isSelected = selectedExpertUuids.has(expert.uuid);
        const isDisabled = !isSelected && disableUnselected;
        return (
          <button
            key={expert.uuid}
            type="button"
            disabled={isDisabled}
            onClick={() => onSelect(expert)}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
              "hover:bg-background-4 hover:cursor-pointer",
              {
                "bg-background-3": isSelected,
                "cursor-not-allowed opacity-40 hover:bg-transparent":
                  isDisabled,
              },
            )}
          >
            <div className="relative shrink-0">
              <ExpertAvatar expert={expert} size="lg" />
              {isSelected && (
                <span className="border-popover bg-accent absolute -right-0.5 -bottom-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2"></span>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
              <span className="truncate font-semibold">{expert.name}</span>
              <span className="text-foreground-5 truncate text-xs">
                {expert.description}
              </span>
              <span className="text-foreground-5 truncate text-xs uppercase">
                {EXPERT_TYPE_LABELS[expert.expertType]}
              </span>
            </div>
            {isSelected && showAddToProjectTag && (
              <span className="text-accent bg-accent-1 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                Add to workbook
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
